import { HttpClient } from '@adapters/HttpClient';
import {
  WithdrawalResponse,
  WithdrawalsResponse,
  CreateWithdrawalPayload,
  WithdrawalsFilters,
  MarkPaidPayload,
  RejectWithdrawalPayload,
  GeneratePaymentFilesPayload,
  GeneratePaymentFilesResponse,
  MarkPaidBulkPayload,
  MarkPaidBulkResponse,
} from '../models';

const buildAdvancedFiltersParams = (advancedFilters: WithdrawalsFilters['advanced_filters']) => {
  if (!advancedFilters) return {};
  const params: Record<string, string | number> = {};

  advancedFilters.filters.forEach((filter, index) => {
    params[`advanced_filters[filters][${index}][column]`] = filter.column;
    params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

    if (
      filter.value === undefined ||
      filter.value === null ||
      (Array.isArray(filter.value) && filter.value.length === 0)
    ) {
      return;
    }

    if (Array.isArray(filter.value)) {
      filter.value.forEach((value, valueIndex) => {
        params[`advanced_filters[filters][${index}][value][${valueIndex}]`] = value;
      });
      return;
    }

    params[`advanced_filters[filters][${index}][value]`] = filter.value;
  });

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
};

export class WithdrawalsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getWithdrawals(walletId: number | null, filters: WithdrawalsFilters = {}): Promise<WithdrawalsResponse> {
    const { sorting, advanced_filters, page = 1, perPage = 25 } = filters;
    const sortColumn = sorting?.column ?? 'created_at';
    const sortDirection = sorting?.direction ?? 'desc';

    const params: Record<string, string | number> = {
      page,
      per_page: perPage,
      'sorting[column]': sortColumn,
      'sorting[direction]': sortDirection,
    };

    if (walletId) {
      params['wallet_id'] = walletId;
    }

    if (advanced_filters && advanced_filters.filters.length > 0) {
      Object.assign(
        params,
        buildAdvancedFiltersParams({
          logic: advanced_filters.logic ?? 'AND',
          filters: advanced_filters.filters,
        }),
      );
    }

    return await this.httpClient.get<WithdrawalsResponse>(`/withdrawals`, params, {}, true);
  }

  async getWithdrawal(id: number): Promise<WithdrawalResponse> {
    return await this.httpClient.get<WithdrawalResponse>(`/withdrawals/${id}`, {}, {}, true);
  }

  async createWithdrawal(walletId: number, payload: CreateWithdrawalPayload): Promise<WithdrawalResponse> {
    return await this.httpClient.post<WithdrawalResponse>(`/wallets/${walletId}/withdrawals`, payload, true);
  }

  async markCancel(id: number): Promise<WithdrawalResponse> {
    return await this.httpClient.put<WithdrawalResponse>(`/withdrawals/${id}/mark-cancel`, {});
  }

  async markPaid(id: number, payload?: MarkPaidPayload): Promise<WithdrawalResponse> {
    return await this.httpClient.put<WithdrawalResponse>(`/withdrawals/${id}/mark-paid`, payload ?? {});
  }

  async markReject(id: number, payload: RejectWithdrawalPayload): Promise<WithdrawalResponse> {
    return await this.httpClient.put<WithdrawalResponse>(`/withdrawals/${id}/mark-reject`, payload);
  }

  async markReturned(id: number): Promise<WithdrawalResponse> {
    return await this.httpClient.put<WithdrawalResponse>(`/withdrawals/${id}/mark-returned`, {});
  }

  /** POST /withdrawals/payment-files/generate — Genera ficheros de pago */
  async generatePaymentFiles(payload: GeneratePaymentFilesPayload): Promise<GeneratePaymentFilesResponse> {
    return await this.httpClient.post<GeneratePaymentFilesResponse>(
      '/withdrawals/payment-files/generate',
      payload,
      true,
    );
  }

  /** POST /withdrawals/mark-paid-bulk — Marca retiradas como pagadas en bulk */
  async markPaidBulk(payload: MarkPaidBulkPayload): Promise<MarkPaidBulkResponse> {
    return await this.httpClient.post<MarkPaidBulkResponse>('/withdrawals/mark-paid-bulk', payload, true);
  }
}
